import { createContext, useContext, useState } from "react";
import { Outlet, useNavigate, useParams, NavLink } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { usePinGuard } from "@/hooks/use-pin-guard";
import { PinVerifyScreen } from "@/components/kid/pin-verify-screen";
import { Home, Star, Swords } from "lucide-react";
import { cn } from "@/lib/utils";

// ─── KidContext ───────────────────────────────────────────────────────────────

interface KidContextValue {
  childId: string;
}

const KidContext = createContext<KidContextValue | null>(null);

export function useKidContext() {
  const ctx = useContext(KidContext);
  if (!ctx) throw new Error("useKidContext must be used inside KidLayout");
  return ctx;
}

// ─── KidLayout ────────────────────────────────────────────────────────────────

export default function KidLayout() {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { verify } = usePinGuard(user?.clerkUserId ?? null);

  const [showPinVerify, setShowPinVerify] = useState(false);

  if (!childId) return null;

  function handleExitRequest() {
    setShowPinVerify(true);
  }

  function handlePinSuccess() {
    setShowPinVerify(false);
    navigate("/parent");
  }

  return (
    <KidContext.Provider value={{ childId }}>
      <div className="flex flex-col min-h-screen bg-gradient-to-b from-teal-50 to-emerald-50">
        {/* Header */}
        <header className="flex items-center justify-between px-4 pt-safe-top pt-4 pb-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-md">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🎾</span>
            <span className="font-bold text-lg tracking-tight">My Tennis</span>
          </div>
          <button
            onClick={handleExitRequest}
            className="flex items-center gap-1.5 text-sm font-medium bg-white/20 hover:bg-white/30 active:bg-white/40 px-3 py-1.5 rounded-full transition-colors"
          >
            <span>🔒</span>
            <span>Exit</span>
          </button>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto pb-24">
          <Outlet />
        </main>

        {/* Bottom nav */}
        <nav className="fixed bottom-0 inset-x-0 bg-white border-t border-border shadow-lg pb-safe-bottom">
          <div className="flex">
            <KidNavItem
              to={`/kid/${childId}`}
              end
              icon={<Home className="w-6 h-6" />}
              label="Home"
            />
            <KidNavItem
              to={`/kid/${childId}/missions`}
              icon={<Swords className="w-6 h-6" />}
              label="Missions"
            />
            <KidNavItem
              to={`/kid/${childId}/badges`}
              icon={<Star className="w-6 h-6" />}
              label="Badges"
            />
          </div>
        </nav>

        {/* PIN verify overlay — renders on top of everything */}
        {showPinVerify && (
          <PinVerifyScreen
            title="Enter parent PIN to exit"
            onVerify={verify}
            onSuccess={handlePinSuccess}
          />
        )}
      </div>
    </KidContext.Provider>
  );
}

// ─── KidNavItem ───────────────────────────────────────────────────────────────

interface KidNavItemProps {
  to: string;
  end?: boolean;
  icon: React.ReactNode;
  label: string;
}

function KidNavItem({ to, end, icon, label }: KidNavItemProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex flex-1 flex-col items-center justify-center gap-1 py-3 text-xs font-semibold transition-colors",
          isActive
            ? "text-emerald-600"
            : "text-muted-foreground hover:text-foreground"
        )
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}
